/* Shared PDP cart state. Add responses contain only added lines, so reread the cart. */
(function () {
  if (window.UqoraPdpCartStatus) return;

  const entries = new Set();
  let cart = null;
  let request = null;
  let revision = 0;
  let unsubscribe = null;
  const rebuyEvents = ['rebuy:cart.add', 'rebuy:cart.change', 'rebuy:cart.remove'];

  try {
    const seed = document.querySelector('[data-pdp-cart-state]');
    if (seed) cart = JSON.parse(seed.textContent);
  } catch (_) { /* A failed seed leaves the normal add action available. */ }

  const planId = (item) => String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '');
  const matches = (line, item) =>
    String(line.variant_id || line.id) === String(item.id) &&
    planId(line) === planId(item) && Number(line.quantity) >= Number(item.quantity || 1);

  function contains(items) {
    if (!cart || !items?.length || items.some((item) => !item.id)) return false;
    const bundleHandle = items[0].properties?._bundle_handle;
    if (!bundleHandle) return items.every((item) => cart.items.some((line) => matches(line, item)));

    // Scattered components or a partly removed bundle do not count as a complete PDP bundle.
    const groups = new Map();
    cart.items.forEach((line) => {
      const properties = line.properties || {};
      if (properties._bundle_handle !== bundleHandle || !properties._bundle_id) return;
      const group = groups.get(properties._bundle_id) || [];
      group.push(line);
      groups.set(properties._bundle_id, group);
    });
    return Array.from(groups.values()).some((group) => items.every((item) => group.some((line) =>
      matches(line, item) && line.properties._bundle_role === item.properties._bundle_role
    )));
  }

  function sync(entry) {
    if (!entry.owner.isConnected || entry.pending?.()) return;
    const available = !entry.button.disabled && entry.button.getAttribute('aria-disabled') !== 'true';
    let items = [];
    try { items = entry.getItems(); } catch (_) { /* Copy must not interrupt cart/offer subscribers. */ }
    const inCart = available && contains(items);
    const changed = entry.button.dataset.uqInCart !== String(inCart);
    entry.button.dataset.uqInCart = String(inCart);
    if (available) {
      entry.label.textContent = inCart ? window.variantStrings.itemAlreadyInCart : entry.normalLabel;
    }
    if (inCart) {
      entry.button.setAttribute('aria-label', window.variantStrings.itemAlreadyInCartAddAnother);
    } else if (entry.ariaLabel === null) {
      entry.button.removeAttribute('aria-label');
    } else {
      entry.button.setAttribute('aria-label', entry.ariaLabel);
    }
    if (changed) {
      entry.status.textContent = inCart ? window.variantStrings.itemAlreadyInCartAddAnother : '';
      entry.owner.dispatchEvent(new CustomEvent('uqora:pdp-cart-status', { bubbles: true }));
    }
  }

  const syncAll = () => entries.forEach(sync);
  const isFullCart = (data) => Array.isArray(data?.items) && typeof data.item_count === 'number';

  function refresh(data) {
    const currentRevision = ++revision;
    if (isFullCart(data)) {
      cart = data;
      syncAll();
      return Promise.resolve();
    }
    if (request) return request;
    request = fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Cart state unavailable');
        return response.json();
      })
      .then((data) => {
        if (currentRevision !== revision) return;
        if (!isFullCart(data)) throw new Error('Incomplete cart state');
        cart = data;
        syncAll();
      })
      .catch(() => {
        if (currentRevision !== revision) return;
        cart = null;
        syncAll();
      })
      .finally(() => {
        request = null;
        if (currentRevision !== revision && entries.size) refresh();
      });
    return request;
  }

  const onCartChange = (event) => refresh(event?.cartData);
  const onResume = () => refresh();

  function listen() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, onCartChange);
    }
    rebuyEvents.forEach((event) => document.addEventListener(event, onCartChange));
    window.addEventListener('pageshow', onResume);
    window.addEventListener('focus', onResume);
  }

  function stopListening() {
    unsubscribe?.();
    unsubscribe = null;
    rebuyEvents.forEach((event) => document.removeEventListener(event, onCartChange));
    window.removeEventListener('pageshow', onResume);
    window.removeEventListener('focus', onResume);
  }

  window.UqoraPdpCartStatus = {
    connect(owner, button, getItems, { label = button, pending, normalLabel = label.textContent } = {}) {
      const status = document.createElement('span');
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      button.after(status);
      const entry = { owner, button, getItems, label, pending, normalLabel, status, ariaLabel: button.getAttribute('aria-label') };
      if (!entries.size) listen();
      entries.add(entry);
      sync(entry);
      if (!cart) refresh();
      return {
        sync: () => sync(entry),
        disconnect() {
          entries.delete(entry);
          status.remove();
          if (!button.disabled && !pending?.()) label.textContent = normalLabel;
          delete button.dataset.uqInCart;
          if (entry.ariaLabel === null) button.removeAttribute('aria-label');
          else button.setAttribute('aria-label', entry.ariaLabel);
          if (!entries.size) stopListening();
        },
      };
    },
    refresh,
  };
})();
